import { useState } from "react";
import { Input } from "./ui/input";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import { sentimentLevels, emotionTypes, topicTypes } from "../lib/data";
import { Search, Filter, X } from "lucide-react";

// --- CHANGE START ---
// Corrected the import paths from "../components/..." to "./..."
import { DateRangePicker } from "./DateRangePicker";
import { MultiSelect, OptionType } from "./MultiSelect";
// --- CHANGE END ---
import { DateRange } from "react-day-picker";

// Updated FilterState Interface
export interface FilterState {
  search: string;
  status: string[]; // <-- Changed to array
  sentiment: string[]; // <-- Changed to array
  emotions: string[];
  topics: string[];
  agent: string[]; // <-- Changed to array
  dateRange?: DateRange; // <-- Added dateRange
}

interface DashboardFiltersProps {
  filters: FilterState;
  onFiltersChange: (filters: FilterState) => void;
  agents: Agent[];
}

interface Agent {
  id: string;
  name: string;
  role: string;
}

export function DashboardFilters({
  filters,
  onFiltersChange,
  agents,
}: DashboardFiltersProps) {
  const [isExpanded, setIsExpanded] = useState(true); // Default to true for better visibility

  // Generic update function
  const updateFilter = (key: keyof FilterState, value: any) => {
    onFiltersChange({ ...filters, [key]: value });
  };

  const toggleListFilter = (key: "emotions" | "topics", value: string) => {
    const currentList = filters[key];
    const newList = currentList.includes(value)
      ? currentList.filter((item) => item !== value)
      : [...currentList, value];
    updateFilter(key, newList);
  };

  // New clear function
  const clearFilters = () => {
    onFiltersChange({
      search: "",
      status: [],
      sentiment: [],
      emotions: [],
      topics: [],
      agent: [],
      dateRange: undefined,
    });
  };

  // Updated count logic
  const activeFiltersCount =
    (filters.search ? 1 : 0) +
    filters.status.length +
    filters.sentiment.length +
    filters.emotions.length +
    filters.topics.length +
    filters.agent.length +
    (filters.dateRange ? 1 : 0);

  // Prepare options for multi-select components
  const agentOptions: OptionType[] = agents.map(agent => ({ value: agent.name, label: agent.name }));
  const statusOptions: OptionType[] = [
      { value: "In Progress", label: "In Progress" },
      { value: "Solved", label: "Solved" }
  ];
  const sentimentOptions: OptionType[] = sentimentLevels.map(s => ({ value: s, label: s }));

  return (
    <div className="space-y-4">
      {/* Search and Main Filter Button */}
      <div className="flex items-center gap-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search tickets, clients, or summaries..."
            value={filters.search}
            onChange={(e) => updateFilter("search", e.target.value)}
            className="pl-10"
          />
        </div>
        <Button
          variant="outline"
          onClick={() => setIsExpanded(!isExpanded)}
          className="flex items-center gap-2"
        >
          <Filter className="h-4 w-4" />
          Filters
          {activeFiltersCount > 0 && (
            <Badge variant="secondary" className="ml-1">
              {activeFiltersCount}
            </Badge>
          )}
        </Button>
        {activeFiltersCount > 0 && (
          <Button variant="ghost" onClick={clearFilters} size="sm">
            <X className="h-4 w-4 mr-1" />
            Clear
          </Button>
        )}
      </div>

      {/* Expanded Filters with New Layout */}
      {isExpanded && (
        <div className="p-4 space-y-6 border rounded-lg bg-muted/30">
          
          {/* Row 1: Primary Filters */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="space-y-2 md:col-span-2">
              <label className="text-sm font-medium">Date Range</label>
              <DateRangePicker
                date={filters.dateRange}
                onDateChange={(value) => updateFilter("dateRange", value)}
              />
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium">Status</label>
              <MultiSelect
                options={statusOptions}
                selected={filters.status}
                onChange={(value) => updateFilter("status", value)}
                placeholder="Select status..."
              />
            </div>
          </div>
          
          {/* Row 2: People & Sentiment */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
             <div className="space-y-2">
              <label className="text-sm font-medium">Agent</label>
               <MultiSelect
                options={agentOptions}
                selected={filters.agent}
                onChange={(value) => updateFilter("agent", value)}
                placeholder="Select agents..."
               />
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium">Sentiment</label>
               <MultiSelect
                options={sentimentOptions}
                selected={filters.sentiment}
                onChange={(value) => updateFilter("sentiment", value)}
                placeholder="Select sentiments..."
               />
            </div>
          </div>

          {/* Row 3 & 4: Categorical Filters */}
          <div className="space-y-2">
            <label className="text-sm font-medium">Emotions</label>
            <div className="flex flex-wrap gap-2">
              {emotionTypes.map((emotion) => (
                <Badge
                  key={emotion}
                  variant={filters.emotions.includes(emotion) ? "default" : "outline"}
                  className="cursor-pointer"
                  onClick={() => toggleListFilter("emotions", emotion)}
                >
                  {emotion}
                </Badge>
              ))}
            </div>
          </div>
          <div className="space-y-2">
            <label className="text-sm font-medium">Topics</label>
            <div className="flex flex-wrap gap-2">
              {topicTypes.map((topic) => (
                <Badge
                  key={topic}
                  variant={filters.topics.includes(topic) ? "default" : "outline"}
                  className="cursor-pointer"
                  onClick={() => toggleListFilter("topics", topic)}
                >
                  {topic}
                </Badge>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}